import {
	type EntitlementGrant,
	EntitlementsEditor,
} from "@/components/admin/EntitlementsEditor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AdminService } from "@/gen/omnios/v1/admin_connect";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { cn } from "@/lib/utils";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/admin/billing/$id")({
	component: PlanDetailPage,
});

const adminClient = createClient(AdminService, transport);

function PlanDetailPage() {
	const { t } = useTranslation("admin");
	const { id } = Route.useParams();
	const qc = useQueryClient();
	const [tab, setTab] = useState<"general" | "pricing" | "grants">("general");

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "billing-plan", id],
		queryFn: () => adminClient.getBillingPlan({ planId: id }),
	});

	const plan = data?.plan;
	const grants = data?.grants ?? [];

	useBreadcrumbs([
		{ label: t("admin_root"), href: "/admin" },
		{ label: t("billing_title"), href: "/admin/billing" },
		{ label: plan?.name ?? "..." },
	]);

	const invalidate = () =>
		qc.invalidateQueries({ queryKey: ["admin", "billing-plan", id] });

	if (isLoading || !plan) {
		return (
			<div className="flex justify-center py-12">
				<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
			</div>
		);
	}

	const TABS = [
		{ key: "general" as const, label: t("plan_tab_general") },
		{ key: "pricing" as const, label: t("plan_tab_pricing") },
		{ key: "grants" as const, label: t("plan_tab_grants") },
	];

	return (
		<div className="space-y-0">
			<div className="flex items-center gap-2 mb-4">
				<Badge variant="secondary" className="font-mono text-[10px] shrink-0">
					{plan.slug}
				</Badge>
				<Badge
					variant={plan.isPublic ? "default" : "outline"}
					className="text-[10px] shrink-0"
				>
					{plan.isPublic ? t("billing_plan_public") : t("billing_plan_hidden")}
				</Badge>
			</div>

			{/* Tab bar */}
			<nav className="-mx-6 flex border-b bg-muted/30 mb-5">
				{TABS.map((t) => (
					<Button
						key={t.key}
						variant="ghost"
						size="sm"
						onClick={() => setTab(t.key)}
						className={cn(
							"px-5 py-2.5 rounded-none border-b-2 -mb-px",
							tab === t.key
								? "border-primary text-foreground bg-background"
								: "border-transparent text-muted-foreground hover:text-foreground",
						)}
					>
						{t.label}
					</Button>
				))}
			</nav>

			{/* Tab content */}
			{tab === "general" && <GeneralTab plan={plan} onSaved={invalidate} />}
			{tab === "pricing" && <PricingTab plan={plan} onSaved={invalidate} />}
			{tab === "grants" && (
				<GrantsTab planId={plan.id} grants={grants} onChanged={invalidate} />
			)}
		</div>
	);
}

// ── General ──

function GeneralTab({
	plan,
	onSaved,
}: {
	plan: {
		id: string;
		name: string;
		description: string;
		trialDays: number;
		isPublic: boolean;
		sortOrder: number;
	};
	onSaved: () => void;
}) {
	const { t } = useTranslation("admin");
	const [name, setName] = useState(plan.name);
	const [description, setDescription] = useState(plan.description);
	const [trialDays, setTrialDays] = useState(String(plan.trialDays));
	const [isPublic, setIsPublic] = useState(plan.isPublic);
	const [sortOrder, setSortOrder] = useState(String(plan.sortOrder));

	useEffect(() => {
		setName(plan.name);
		setDescription(plan.description);
		setTrialDays(String(plan.trialDays));
		setIsPublic(plan.isPublic);
		setSortOrder(String(plan.sortOrder));
	}, [plan]);

	const mutation = useMutation({
		mutationFn: () =>
			adminClient.updateBillingPlan({
				planId: plan.id,
				name,
				description,
				isPublic,
				trialDays: Number.parseInt(trialDays) || 0,
				sortOrder: Number.parseInt(sortOrder) || 0,
			}),
		onSuccess: onSaved,
	});

	return (
		<div className="space-y-4 max-w-lg">
			<div className="space-y-2">
				<Label>{t("billing_plan_name")}</Label>
				<Input value={name} onChange={(e) => setName(e.target.value)} />
			</div>
			<div className="space-y-2">
				<Label>{t("billing_plan_description")}</Label>
				<Input
					value={description}
					onChange={(e) => setDescription(e.target.value)}
				/>
			</div>
			<div className="grid grid-cols-2 gap-4">
				<div className="space-y-2">
					<Label>{t("billing_trial_days")}</Label>
					<Input
						type="number"
						value={trialDays}
						onChange={(e) => setTrialDays(e.target.value)}
					/>
				</div>
				<div className="space-y-2">
					<Label>{t("plan_detail_sort_order")}</Label>
					<Input
						type="number"
						value={sortOrder}
						onChange={(e) => setSortOrder(e.target.value)}
					/>
				</div>
			</div>
			<div className="flex items-center gap-2">
				<Switch checked={isPublic} onCheckedChange={setIsPublic} />
				<Label>{t("billing_plan_is_public")}</Label>
			</div>
			<Button
				onClick={() => mutation.mutate()}
				disabled={mutation.isPending || !name}
			>
				{mutation.isPending && (
					<Loader2 className="h-4 w-4 animate-spin mr-1.5" />
				)}
				{t("common:save")}
			</Button>
		</div>
	);
}

// ── Pricing ──

function PricingTab({
	plan,
	onSaved,
}: {
	plan: {
		id: string;
		prices: {
			period: string;
			baseAmount: bigint;
			perSeatAmount: bigint;
			includedSeats: number;
		}[];
	};
	onSaved: () => void;
}) {
	const { t } = useTranslation("admin");

	return (
		<div className="space-y-6 max-w-lg">
			<PricePeriodForm
				planId={plan.id}
				period="monthly"
				label={t("plan_detail_price_monthly")}
				existing={plan.prices.find((p) => p.period === "monthly")}
				onSaved={onSaved}
			/>
			<div className="border-t" />
			<PricePeriodForm
				planId={plan.id}
				period="annual"
				label={t("plan_detail_price_annual")}
				existing={plan.prices.find((p) => p.period === "annual")}
				onSaved={onSaved}
			/>
		</div>
	);
}

function PricePeriodForm({
	planId,
	period,
	label,
	existing,
	onSaved,
}: {
	planId: string;
	period: string;
	label: string;
	existing?: {
		baseAmount: bigint;
		perSeatAmount: bigint;
		includedSeats: number;
	};
	onSaved: () => void;
}) {
	const { t } = useTranslation("admin");
	const [base, setBase] = useState(String(Number(existing?.baseAmount ?? 0)));
	const [perSeat, setPerSeat] = useState(
		String(Number(existing?.perSeatAmount ?? 0)),
	);
	const [seats, setSeats] = useState(String(existing?.includedSeats ?? 0));

	useEffect(() => {
		setBase(String(Number(existing?.baseAmount ?? 0)));
		setPerSeat(String(Number(existing?.perSeatAmount ?? 0)));
		setSeats(String(existing?.includedSeats ?? 0));
	}, [existing]);

	const mutation = useMutation({
		mutationFn: () =>
			adminClient.setPlanPrice({
				planId,
				period,
				baseAmount: BigInt(base || "0"),
				perSeatAmount: BigInt(perSeat || "0"),
				includedSeats: Number.parseInt(seats) || 0,
			}),
		onSuccess: onSaved,
	});

	return (
		<div className="space-y-3">
			<h3 className="text-sm font-medium">{label}</h3>
			<div className="grid grid-cols-3 gap-3">
				<div className="space-y-1">
					<Label className="text-xs text-muted-foreground">
						{t("plan_detail_price_base")}
					</Label>
					<Input
						type="number"
						value={base}
						onChange={(e) => setBase(e.target.value)}
					/>
				</div>
				<div className="space-y-1">
					<Label className="text-xs text-muted-foreground">
						{t("plan_detail_price_per_seat")}
					</Label>
					<Input
						type="number"
						value={perSeat}
						onChange={(e) => setPerSeat(e.target.value)}
					/>
				</div>
				<div className="space-y-1">
					<Label className="text-xs text-muted-foreground">
						{t("plan_detail_price_included_seats")}
					</Label>
					<Input
						type="number"
						value={seats}
						onChange={(e) => setSeats(e.target.value)}
					/>
				</div>
			</div>
			<Button
				variant="outline"
				size="sm"
				onClick={() => mutation.mutate()}
				disabled={mutation.isPending}
			>
				{mutation.isPending && (
					<Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
				)}
				{t("common:save")}
			</Button>
		</div>
	);
}

// ── Grants ──

function GrantsTab({
	planId,
	grants,
	onChanged,
}: {
	planId: string;
	grants: {
		id: string;
		featureSlug: string;
		pluginSlug: string;
		metricCode: string;
		grantType: string;
		includedQuantity: bigint;
		overageUnitPrice: bigint;
	}[];
	onChanged: () => void;
}) {
	const [saving, setSaving] = useState(false);

	const mapped: EntitlementGrant[] = grants.map((g) => ({
		id: g.id,
		featureSlug: g.featureSlug || undefined,
		pluginSlug: g.pluginSlug || undefined,
		metricCode: g.metricCode || undefined,
		grantType: g.grantType,
		includedQuantity: g.includedQuantity,
		overageUnitPrice: g.overageUnitPrice,
	}));

	const handleAdd = async (grant: Omit<EntitlementGrant, "id">) => {
		setSaving(true);
		try {
			await adminClient.setPlanGrant({
				planId,
				featureSlug: grant.featureSlug ?? "",
				pluginSlug: grant.pluginSlug ?? "",
				metricCode: grant.metricCode ?? "",
				grantType: grant.grantType,
				includedQuantity: BigInt(Number(grant.includedQuantity ?? 0)),
				overageUnitPrice: BigInt(Number(grant.overageUnitPrice ?? 0)),
			});
			onChanged();
		} finally {
			setSaving(false);
		}
	};

	const handleRemove = async (grantId: string) => {
		setSaving(true);
		try {
			await adminClient.removePlanGrant({ grantId });
			onChanged();
		} finally {
			setSaving(false);
		}
	};

	return (
		<EntitlementsEditor
			grants={mapped}
			onAdd={handleAdd}
			onRemove={handleRemove}
			saving={saving}
		/>
	);
}
